'use client'

import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import type { ButtonHTMLAttributes, ForwardedRef } from 'react'
import { forwardRef } from 'react'
import { cn } from '../lib/utils.ts'

/**
 * Variant matrix for the primary action element.
 *
 * Deliberately NO font-size in the base: sizes own it, so a caller can swap
 * `size` without fighting a base `text-sm` that tailwind-merge would have to
 * resolve.
 */
export const buttonVariants = cva(
  'ring-offset-background inline-flex items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        destructive: 'bg-destructive text-white hover:bg-destructive/90',
        link: 'text-primary underline-offset-4 hover:underline',
        /**
         * The one FLOU-branded action: the violet → magenta → pink ramp, lifted
         * slightly on hover. `bg-[image:…]` (not `bg-[…]`) because Tailwind
         * cannot tell a gradient from a colour behind a `var()`.
         */
        brand:
          'bg-[image:var(--flou-gradient-solid)] font-semibold text-white transition-transform duration-300 hover:scale-[1.04]',
      },
      size: {
        default: 'h-10 px-4 py-2 text-sm',
        sm: 'h-9 rounded-md px-3 text-sm',
        lg: 'h-11 rounded-md px-8 text-base',
        icon: 'size-10',
        /** Padding-driven (no fixed height), fully round — the marketing CTA. */
        pill: 'rounded-full px-4 py-2 text-cta',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /**
   * Render the single child element as the underlying node (Radix `Slot`)
   * instead of a `<button>` — the way a `next/link` gets button styling
   * without nesting a `<button>` inside an `<a>`.
   */
  asChild?: boolean
}

export const Button = forwardRef(function Button(
  { className, variant, size, asChild = false, type, ...props }: ButtonProps,
  ref: ForwardedRef<HTMLButtonElement>,
) {
  if (asChild) {
    return (
      <Slot ref={ref} className={cn(buttonVariants({ variant, size }), className)} {...props} />
    )
  }
  return (
    <button
      ref={ref}
      // Without this a <button> inside a <form> defaults to submit, which is
      // the single most common accidental-submit bug in this codebase.
      type={type ?? 'button'}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  )
})

Button.displayName = 'Button'
